// QUICK BOOKING — a polished 3-step shortcut over the EXISTING booking backend.
// Reuses the same endpoints the full ManageInventory wizard uses (getRoomsByHotel
// / SelectRoom / packages / bookPackage), so the booking, ledger, invoice and
// reports are identical. The full 5-step flow stays completely untouched.
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import config from '../../config';
import { isGstApplicable } from './gstSettings';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
// Currency symbol from the company - never hard-code a rupee sign.
const Companycurrency = company?.currency ?? '';
const headers = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${sessionStorage.getItem('token')}`,
});

const steps = ['Room Selection', 'Package Selection', 'Guest & Confirm'];

const nightsBetween = (a, b) => {
  if (!a || !b) return 0;
  const d = Math.round((new Date(b) - new Date(a)) / 86400000);
  return d > 0 ? d : 0;
};
const gstRateFor = (basePerNight) => {
  if (!isGstApplicable()) return 0;
  if (!basePerNight || basePerNight <= 0) return 0;
  return basePerNight * 1.05 >= 7875 ? 18 : 5;
};

export default function QuickBooking() {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  // Step 1
  const [hotels, setHotels] = useState([]);
  const [hotelId, setHotelId] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [allRooms, setAllRooms] = useState([]); // [{roomnumber, status, type}]
  const [roomType, setRoomType] = useState(''); // locked once a room is picked
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [catPopup, setCatPopup] = useState(null); // #10: { type, rooms } for multi-room pick

  // Step 2
  const [packages, setPackages] = useState([]);
  const [pkg, setPkg] = useState(null);
  const [price, setPrice] = useState(null);

  // Step 3
  const [guest, setGuest] = useState({ firstName: '', lastName: '', mobile: '' });
  const [remark, setRemark] = useState('');
  const [bookingNo, setBookingNo] = useState(null);

  const nights = nightsBetween(checkIn, checkOut);
  const qty = selectedRooms.length;
  const basePerNight = Number(price?.price || 0);
  const gstRate = gstRateFor(basePerNight);
  const baseTotal = Math.round(basePerNight * qty * nights);
  const gstAmt = Math.round((baseTotal * gstRate) / 100);
  const finalWithGst = baseTotal + gstAmt;

  /* ── Load hotels + auto-select the first ── */
  useEffect(() => {
    if (!company?.id) return;
    fetch(`${config.API_BASE_URL}/Home/GetHotelsdropdown/${company.id}`, { headers: headers() })
      .then((r) => r.json())
      .then((d) => {
        const list = Array.isArray(d) ? d : [];
        setHotels(list);
        if (list.length) setHotelId(list[0].id); // first hotel pre-selected
      })
      .catch(() => {});
  }, []);

  /* ── Find Rooms → load ALL rooms across every type at once ── */
  const findRooms = async () => {
    if (!hotelId) return toast.warn('Select a hotel');
    if (!checkIn || !checkOut) return toast.warn('Select check-in & check-out dates');
    if (nights <= 0) return toast.warn('Check-out must be after check-in');
    setBusy(true);
    setAllRooms([]);
    setRoomType('');
    setSelectedRooms([]);
    try {
      // 1) room types for the hotel
      const tr = await fetch(
        `${config.API_BASE_URL}/Home/getRoomsByHotel/${hotelId}?id=${company.id}&date=${checkIn}`,
        { headers: headers() },
      );
      const types = (await tr.json()) || [];
      if (!Array.isArray(types) || types.length === 0) {
        toast.info('No rooms found for this hotel / date');
        return;
      }
      // 2) actual rooms for every type — merged into one flat list (tagged)
      const lists = await Promise.all(
        types.map((t) =>
          fetch(`${config.API_BASE_URL}/Home/SelectRoom/${company.id}`, {
            method: 'POST',
            headers: headers(),
            body: JSON.stringify({ hotelId: Number(hotelId), roomType: t.roomType, date: checkIn }),
          })
            .then((r) => (r.ok ? r.json() : []))
            .then((rooms) =>
              (Array.isArray(rooms) ? rooms : []).map((rm) => ({ ...rm, type: t.roomType })),
            )
            .catch(() => []),
        ),
      );
      const flat = lists.flat();
      setAllRooms(flat);
      if (flat.length === 0) toast.info('No rooms found');
    } catch {
      toast.error('Failed to load rooms');
    } finally {
      setBusy(false);
    }
  };

  // Pick a room directly. The FIRST pick locks the room type (packages are
  // per-type); rooms of other types are then disabled.
  const toggleRoom = (room) => {
    if (room.status !== 'Available') return;
    if (roomType && room.type !== roomType) {
      toast.info(`You can only mix rooms of the same type (${roomType}).`);
      return;
    }
    setSelectedRooms((prev) => {
      const has = prev.includes(room.roomnumber);
      const next = has ? prev.filter((x) => x !== room.roomnumber) : [...prev, room.roomnumber];
      setRoomType(next.length ? room.type : ''); // unlock when nothing selected
      return next;
    });
  };

  /* ── Step 2: packages for the locked room type ── */
  const goToPackages = async () => {
    if (selectedRooms.length === 0) return toast.warn('Select at least one room');
    setBusy(true);
    try {
      const url = `${config.API_BASE_URL}/Home/GetPackageReportforbookings?hotelId=${hotelId}&companyid=${company.id}&roomTypeId=${roomType}&checkInDate=${checkIn}&checkOutDate=${checkOut}`;
      const r = await fetch(url, { headers: headers() });
      const d = await r.json();
      setPackages(Array.isArray(d) ? d : []);
      setPkg(null);
      setPrice(null);
      if (!Array.isArray(d) || d.length === 0)
        toast.warn('No packages found for this room type / dates');
      setStep(1);
    } catch {
      toast.error('Failed to load packages');
    } finally {
      setBusy(false);
    }
  };

  const pickPackage = (p) => {
    setPkg(p);
    const list = p.memberWisePrices || [];
    setPrice(list.length ? list[0] : null);
  };

  /* ── Step 3: confirm (reuses bookPackage) ── */
  const confirm = async () => {
    const uid = Number(sessionStorage.getItem('userId')) || 0;
    if (!uid) return toast.error('Session expired — please log in again.');
    if (!guest.firstName.trim()) return toast.warn('Guest name is required');
    if (!/^\d{10}$/.test(String(guest.mobile).trim()))
      return toast.warn('Enter a valid 10-digit mobile number');
    if (!pkg || !price) return toast.warn('Select a package');

    const payload = {
      HotelId: Number(hotelId),
      Companyid: company.id,
      Userid: uid,
      RoomId: Number(selectedRooms[0]) || 0,
      RoomType: roomType,
      PackageId: Number(pkg.packageId),
      SelectedRoomNumbers: selectedRooms,
      Qty: qty,
      CheckInDate: checkIn,
      CheckOutDate: checkOut,
      Nights: nights,
      Remark: remark || '',
      SelectedPrice: {
        AdultCount: Number(price.adultCount || 0),
        ChildCount: Number(price.childCount || 0),
        PackageId: Number(pkg.packageId),
        OriginalPrice: basePerNight,
        DiscountPerRoom: 0,
        FinalPrice: basePerNight,
        Price: baseTotal,
      },
      GuestDetails: {
        BillingType: 'Individual',
        Title: '',
        FirstName: guest.firstName.trim(),
        LastName: guest.lastName.trim(),
        DOB: null,
        Age: null,
        Email: '',
        MobileNumber: String(guest.mobile).trim(),
        Address: '',
        City: '',
        State: '',
        // Full field set (empty) — SaveBookingAsync inserts these into non-null
        // columns; omitting them made the save fail with "Failed to save booking".
        CompanyName: '',
        GstNo: '',
        CompanyAddress: '',
        CompanyCity: '',
        CompanyState: '',
        CompanyMobile: '',
        CompanyEmail: '',
        CardType: '',
        CardNumber: '',
        ReferenceNo: '',
        Country: '',
        IDType: '',
        IDName: '',
        IDNumber: '',
        Nationality: '',
        PassportNumber: '',
        adultCount: Number(price.adultCount || 1) || 1,
        childCount: Number(price.childCount || 0),
      },
      OtherGuests: [],
      Addons: [],
      Discount: { Type: 'Fixed', Value: 0 },
      PaidAmount: 0,
      Payments: [{ Mode: 'Due', Amount: finalWithGst }],
      price: baseTotal,
      TotalPrice: baseTotal,
      Finalbookingprice: finalWithGst,
      PaymentMode: 'Due',
      finalpriceWithGST: finalWithGst,
      BookingSource: 'WalkIn',
      AgentDetails: null,
      AgentBookingType: 'Direct',
      AgentBooking: null,
      Coupon: null,
    };

    setBusy(true);
    try {
      const r = await fetch(`${config.API_BASE_URL}/Home/bookPackage`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(payload),
      });
      const text = await r.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch {
        result = { message: text };
      }
      const ok =
        r.ok &&
        result.message !== 'Failed to save booking' &&
        ((result?.message || '').toLowerCase().includes('booking successfuly') ||
          result?.success ||
          result?.bookingId);
      if (ok) {
        setBookingNo(result.bookingId || result.id || null);
        toast.success('Booking confirmed!');
        setStep(3);
      } else {
        console.error('[QuickBooking] save failed', { status: r.status, result, payload });
        toast.error(`Booking failed: ${result?.message || 'server ' + r.status}`);
      }
    } catch {
      toast.error('Booking failed');
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setStep(0);
    setAllRooms([]);
    setRoomType('');
    setSelectedRooms([]);
    setPackages([]);
    setPkg(null);
    setPrice(null);
    setGuest({ firstName: '', lastName: '', mobile: '' });
    setRemark('');
    setBookingNo(null);
  };

  // group rooms by type for a clean, "pick a room directly" grid
  const grouped = allRooms.reduce((m, r) => {
    (m[r.type] = m[r.type] || []).push(r);
    return m;
  }, {});

  // #10: QUICK BOOKING BY CATEGORY. Pick a room CATEGORY and the system
  // auto-allocates the first available room; if several are free it opens a
  // room-selection popup. Reuses toggleRoom so the booking logic is identical.
  const availableOfType = (type) =>
    allRooms.filter((r) => r.type === type && r.status === 'Available');

  const quickPickCategory = (type) => {
    if (roomType && roomType !== type) {
      toast.info(`You're already selecting ${roomType} rooms.`);
      return;
    }
    const avail = availableOfType(type).filter(
      (r) => !selectedRooms.includes(r.roomnumber),
    );
    if (avail.length === 0) {
      toast.info(`No available rooms in ${type}.`);
      return;
    }
    if (avail.length === 1) {
      toggleRoom(avail[0]);
      toast.success(`Allocated Room ${avail[0].roomnumber} (${type})`);
      return;
    }
    setCatPopup({ type, rooms: avail });
  };

  const card = { borderRadius: 3, border: '1px solid #E5E7EB', background: '#fff' };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 }, maxWidth: 880, mx: 'auto' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1.3,
          mb: 1.5,
          borderRadius: 2,
          color: '#fff',
          background: 'linear-gradient(135deg,#0b1120 0%,#0f172a 40%,#1e3a8a 100%)',
        }}>
        <Typography variant='subtitle1' fontWeight={900}>
          ⚡ Quick Booking
        </Typography>
      </Paper>

      <Stepper activeStep={step} alternativeLabel sx={{ mb: 2 }}>
        {steps.map((s) => (
          <Step key={s}>
            <StepLabel>{s}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* ── STEP 1: ROOM SELECTION (direct) ── */}
      {step === 0 && (
        <Paper elevation={0} sx={{ ...card, p: 2 }}>
          <Grid container spacing={2} alignItems='flex-end'>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth size='small' label='Hotel' value={hotelId}
                onChange={(e) => setHotelId(e.target.value)}>
                {hotels.map((h) => (
                  <MenuItem key={h.id} value={h.id}>{h.hotelName || h.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size='small' type='date' label='Check-in' InputLabelProps={{ shrink: true }}
                value={checkIn}
                onChange={(e) => {
                  const v = e.target.value;
                  setCheckIn(v);
                  // Auto-fill check-out to the NEXT day if empty or not after check-in.
                  if (v && (!checkOut || new Date(checkOut) <= new Date(v))) {
                    const d = new Date(v);
                    d.setDate(d.getDate() + 1);
                    setCheckOut(d.toISOString().slice(0, 10));
                  }
                }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size='small' type='date' label='Check-out' InputLabelProps={{ shrink: true }}
                value={checkOut} onChange={(e) => setCheckOut(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button fullWidth variant='contained' onClick={findRooms} disabled={busy}>
                Find Rooms
              </Button>
            </Grid>
          </Grid>

          {busy && <Box sx={{ textAlign: 'center', mt: 3 }}><CircularProgress size={24} /></Box>}

          {!busy && allRooms.length > 0 && (
            <>
              <Divider sx={{ my: 2.5 }} />

              {/* #10: Book by category — auto-allocates the best available room. */}
              <Typography fontWeight={800} sx={{ mb: 0.75 }}>
                ⚡ Book by Category
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {Object.keys(grouped).map((type) => {
                  const availCount = availableOfType(type).filter(
                    (r) => !selectedRooms.includes(r.roomnumber),
                  ).length;
                  const disabled =
                    availCount === 0 || (roomType && roomType !== type);
                  return (
                    <Chip
                      key={`cat-${type}`}
                      label={`${type} (${availCount} free)`}
                      color='primary'
                      variant='outlined'
                      disabled={disabled}
                      onClick={() => quickPickCategory(type)}
                      sx={{ fontWeight: 700 }}
                    />
                  );
                })}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography fontWeight={800}>Or Pick Room(s)</Typography>
                {roomType && <Chip size='small' color='primary' label={`Type: ${roomType}`} />}
              </Box>
              {Object.entries(grouped).map(([type, list]) => {
                const disabled = roomType && type !== roomType;
                return (
                  <Box key={type} sx={{ mb: 2, opacity: disabled ? 0.45 : 1 }}>
                    <Typography variant='caption' sx={{ fontWeight: 700, color: '#6B7280' }}>
                      {type}
                    </Typography>
                    <Grid container spacing={1} sx={{ mt: 0.2 }}>
                      {list.map((room) => {
                        const occ = room.status !== 'Available';
                        const sel = selectedRooms.includes(room.roomnumber);
                        return (
                          <Grid item xs={3} sm={2} md={1.5} key={`${type}-${room.roomnumber}`}>
                            <Box
                              onClick={() => toggleRoom(room)}
                              sx={{
                                p: 1.1,
                                textAlign: 'center',
                                borderRadius: 2,
                                fontWeight: 800,
                                color: '#fff',
                                fontSize: 14,
                                cursor: occ || disabled ? 'not-allowed' : 'pointer',
                                boxShadow: sel ? '0 4px 14px rgba(26,86,219,0.35)' : 'none',
                                transform: sel ? 'translateY(-2px)' : 'none',
                                transition: 'all .15s',
                                bgcolor: occ ? '#dc2626' : sel ? '#1A56DB' : '#16a34a',
                              }}>
                              {room.roomnumber}
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                );
              })}
              <Box sx={{ display: 'flex', gap: 2, mt: 1, fontSize: 12, color: '#6B7280' }}>
                <span>🟢 Available</span> <span>🔵 Selected</span> <span>🔴 Occupied</span>
              </Box>
            </>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button variant='contained' size='large' onClick={goToPackages}
              disabled={busy || selectedRooms.length === 0}>
              Next: Package →
            </Button>
          </Box>
        </Paper>
      )}

      {/* ── STEP 2: PACKAGE ── */}
      {step === 1 && (
        <Paper elevation={0} sx={{ ...card, p: 2 }}>
          <Grid container spacing={2}>
            {packages.length === 0 && (
              <Grid item xs={12}><Typography color='text.secondary'>No packages available.</Typography></Grid>
            )}
            {packages.map((p) => (
              <Grid item xs={12} sm={6} md={4} key={p.packageId}>
                <Paper onClick={() => pickPackage(p)} elevation={0}
                  sx={{
                    p: 2.2, cursor: 'pointer', borderRadius: 3,
                    border: `2px solid ${pkg?.packageId === p.packageId ? '#1A56DB' : '#E5E7EB'}`,
                    transition: 'all .15s',
                    '&:hover': { borderColor: '#1A56DB', transform: 'translateY(-2px)' },
                  }}>
                  <Typography fontWeight={800}>{p.packageName}</Typography>
                  <Typography variant='h6' color='primary' mt={1}>
                    {Companycurrency} {p.lowestPrice ?? p.memberWisePrices?.[0]?.price ?? 0}
                    <Typography component='span' variant='caption' color='text.secondary'> /room/night</Typography>
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {pkg && (pkg.memberWisePrices || []).length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography fontWeight={800} mb={1}>Occupancy / Price</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {pkg.memberWisePrices.map((m, i) => (
                  <Chip key={i} label={`${m.adultCount}A ${m.childCount}C — ${Companycurrency}${m.price}`}
                    color={price === m ? 'primary' : 'default'}
                    variant={price === m ? 'filled' : 'outlined'}
                    onClick={() => setPrice(m)} />
                ))}
              </Box>
            </>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button onClick={() => setStep(0)}>← Back</Button>
            <Button variant='contained' size='large' onClick={() => setStep(2)} disabled={!pkg || !price}>
              Next: Guest →
            </Button>
          </Box>
        </Paper>
      )}

      {/* ── STEP 3: GUEST & CONFIRM ── */}
      {step === 2 && (
        <Paper elevation={0} sx={{ ...card, p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size='small' label='Guest First Name *' value={guest.firstName}
                onChange={(e) => setGuest({ ...guest, firstName: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size='small' label='Last Name' value={guest.lastName}
                onChange={(e) => setGuest({ ...guest, lastName: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size='small' label='Mobile (10 digit) *' value={guest.mobile}
                onChange={(e) => setGuest({ ...guest, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size='small' label='Remark (optional)' value={remark}
                onChange={(e) => setRemark(e.target.value)} />
            </Grid>
          </Grid>

          <Paper elevation={0} sx={{ p: 2, mt: 2, bgcolor: '#F8FAFF', borderRadius: 2, border: '1px solid #E5E7EB' }}>
            <Grid container spacing={1}>
              <Grid item xs={6}><Typography variant='body2'>Rooms: <b>{selectedRooms.join(', ')}</b> ({roomType})</Typography></Grid>
              <Grid item xs={6}><Typography variant='body2'>Nights: <b>{nights}</b></Typography></Grid>
              <Grid item xs={6}><Typography variant='body2'>Package: <b>{pkg?.packageName}</b></Typography></Grid>
              <Grid item xs={6}><Typography variant='body2'>Base: <b>{Companycurrency}{baseTotal}</b></Typography></Grid>
              {/* GST row only for GST companies (India). */}
              {isGstApplicable() && (
                <Grid item xs={6}><Typography variant='body2'>GST ({gstRate}%): <b>{Companycurrency}{gstAmt}</b></Typography></Grid>
              )}
              <Grid item xs={6}><Typography variant='body2' color='primary'>Total: <b>{Companycurrency}{finalWithGst}</b> (Due)</Typography></Grid>
            </Grid>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button onClick={() => setStep(1)}>← Back</Button>
            <Button variant='contained' size='large' color='success' onClick={confirm} disabled={busy}
              startIcon={busy ? <CircularProgress size={16} color='inherit' /> : null}>
              {busy ? 'Booking…' : 'Confirm Booking'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* ── DONE ── */}
      {step === 3 && (
        <Paper elevation={0} sx={{ ...card, p: 3, textAlign: 'center' }}>
          <Typography variant='h3'>✅</Typography>
          <Typography variant='h6' fontWeight={900} mt={1}>Booking Confirmed</Typography>
          {bookingNo && <Typography mt={1} color='text.secondary'>Booking No: <b>#{bookingNo}</b></Typography>}
          <Typography mt={1} color='text.secondary'>
            Room {selectedRooms.join(', ')} • {roomType} • {nights} night(s) • {Companycurrency}{finalWithGst} (Due)
          </Typography>
          <Button variant='contained' size='large' sx={{ mt: 3 }} onClick={reset}>New Quick Booking</Button>
        </Paper>
      )}

      {/* #10: multiple rooms free in the chosen category — pick one (or auto). */}
      <Dialog open={!!catPopup} onClose={() => setCatPopup(null)} fullWidth maxWidth='xs'>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {catPopup?.type} — choose a room
          <Typography sx={{ color: '#6B7280', fontSize: 13 }}>
            {catPopup?.rooms?.length} rooms available
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1}>
            {(catPopup?.rooms || []).map((room) => (
              <Grid item xs={3} key={`pop-${room.roomnumber}`}>
                <Box
                  onClick={() => {
                    toggleRoom(room);
                    setCatPopup(null);
                    toast.success(`Allocated Room ${room.roomnumber}`);
                  }}
                  sx={{
                    p: 1.1,
                    textAlign: 'center',
                    borderRadius: 2,
                    fontWeight: 800,
                    color: '#fff',
                    cursor: 'pointer',
                    bgcolor: '#16a34a',
                    '&:hover': { bgcolor: '#15803d' },
                  }}>
                  {room.roomnumber}
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatPopup(null)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={() => {
              const first = catPopup?.rooms?.[0];
              if (first) {
                toggleRoom(first);
                toast.success(`Allocated Room ${first.roomnumber}`);
              }
              setCatPopup(null);
            }}>
            Auto-allocate best
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
